import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import PhotoStrip from '../../components/review/PhotoStrip';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import { Booking } from '../../data/bookings';
import { ReviewPhoto } from '../../data/reviews';
import { captureWithCamera, pickFromLibrary } from '../../services/photos';
import {
  disputeEvidence,
  Ticket,
  TicketCategory,
  ticketCategories,
} from '../../data/support';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * Raising a request - and, in one particular shape of it, a dispute.
 *
 * ---------------------------------------------------------------------------
 * ONE FORM, TWO JOBS
 * ---------------------------------------------------------------------------
 * A dispute is a ticket against a completed booking with a higher evidence
 * bar, not a separate object - see `data/support.ts`. So this screen is the
 * same form either way; opened with a `booking` it locks the category to
 * "dispute", names the job it is about, and shows what the platform will
 * already be looking at.
 *
 * ---------------------------------------------------------------------------
 * SAYING WHAT WE ALREADY HOLD
 * ---------------------------------------------------------------------------
 * The evidence panel is not decoration. A dispute form that asks for proof
 * without saying what we can already see gets photographs of the timeline and
 * silence about the thing we cannot check. Listing it up front changes what
 * people write.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS REAL
 * ---------------------------------------------------------------------------
 * The form and its validation. `onSubmit` hands a `Ticket` back to the caller,
 * which keeps it in memory - there is no support desk to post to. The photos
 * are real files from the picker with nowhere to upload to.
 */

const MAX_CHARS = 1000;
const MAX_PHOTOS = 4;

type Props = {
  onBack: () => void;
  onSubmit: (ticket: Ticket) => void;
  /** Present when this was opened as "dispute this job". */
  booking?: Booking;
};

function NewTicketScreen({ onBack, onSubmit, booking }: Props) {
  const resp = useResponsive();

  const disputing = !!booking;
  const [category, setCategory] = useState<TicketCategory>(
    disputing ? 'dispute' : 'billing',
  );
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [photos, setPhotos] = useState<ReviewPhoto[]>([]);

  /** Set when Android has refused the camera for good - see `services/photos`. */
  const [cameraBlocked, setCameraBlocked] = useState(false);

  /*
   * Both sources. On a dispute the camera matters more than anywhere else in
   * the app: the evidence is the damaged thing, and it is in front of them.
   */
  const attach = async (from: 'camera' | 'library') => {
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      return;
    }

    const result =
      from === 'camera'
        ? await captureWithCamera(room)
        : await pickFromLibrary(room);

    setCameraBlocked(result.blocked === 'camera-permission');

    if (result.photos.length) {
      setPhotos(current => [...current, ...result.photos]);
    }
  };

  /*
   * A subject and a sentence. Both, because a ticket with only a subject
   * ("Refund") is a ticket ops has to reply to before it can be worked, which
   * costs the customer a day for a question they could have answered here.
   */
  const ready = subject.trim().length > 2 && body.trim().length > 9;

  const submit = () => {
    if (!ready) {
      return;
    }
    const stamp = 'Just now';
    onSubmit({
      id: `t-${Date.now()}`,
      /* Local and obviously provisional. The real reference is minted by the
         backend; inventing a plausible one here would be quoted back to
         support, who would not find it. */
      reference: 'Pending',
      category,
      subject: subject.trim(),
      status: 'open',
      createdOn: stamp,
      updatedOn: stamp,
      bookingRef: booking?.reference,
      messages: [{ id: 'm1', from: 'you', body: body.trim(), when: stamp }],
    });
  };

  return (
    <View style={styles.root}>
      <PageHeader
        title={disputing ? 'Dispute this job' : 'New request'}
        onBack={onBack}
      />

      <ScrollView
        style={styles.scroller}
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* ------------------------------------------------- what it is about */}
        {booking ? (
          <View style={styles.about}>
            <View style={styles.mark}>
              <Icon name="calendar" size={18} color={colors.primary} />
            </View>
            <View style={styles.grow}>
              <Text variant="subtitle" numberOfLines={1}>
                {booking.title}
              </Text>
              <Text variant="caption" color={colors.inkMuted}>
                {booking.reference} · {booking.when}
              </Text>
            </View>
          </View>
        ) : null}

        {/* ------------------------------------------------------- category */}
        {disputing ? null : (
          <View style={styles.block}>
            <Text variant="labelSm" color={colors.inkBody}>
              What is this about?
            </Text>
            {ticketCategories
              .filter(item => item.key !== 'dispute')
              .map(item => {
                const on = item.key === category;
                return (
                  <Pressable
                    key={item.key}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: on }}
                    accessibilityLabel={`${item.label}. ${item.blurb}`}
                    onPress={() => setCategory(item.key)}
                    style={({ pressed }) => [
                      styles.option,
                      on ? styles.optionOn : null,
                      pressed ? styles.pressed : null,
                    ]}
                  >
                    <View style={styles.mark}>
                      <Icon name={item.icon} size={18} color={colors.primary} />
                    </View>
                    <View style={styles.grow}>
                      <Text variant="subtitle">{item.label}</Text>
                      <Text variant="caption" color={colors.inkMuted}>
                        {item.blurb}
                      </Text>
                    </View>
                    <View style={[styles.radio, on ? styles.radioOn : null]}>
                      {on ? (
                        <Icon
                          name="check"
                          size={11}
                          color={colors.onPrimary}
                          strokeWidth={3}
                        />
                      ) : null}
                    </View>
                  </Pressable>
                );
              })}
          </View>
        )}

        {/* -------------------------------------------------------- subject */}
        <View style={styles.block}>
          <Text variant="labelSm" color={colors.inkBody}>
            Subject
          </Text>
          <TextInput
            value={subject}
            onChangeText={next => setSubject(next.slice(0, 90))}
            placeholder={
              disputing ? 'e.g. Bathroom was not cleaned' : 'e.g. Charged twice'
            }
            placeholderTextColor={colors.inkSubtle}
            style={styles.field}
            accessibilityLabel="Subject"
          />
        </View>

        {/* ----------------------------------------------------- the detail */}
        <View style={styles.block}>
          <Text variant="labelSm" color={colors.inkBody}>
            What happened?
          </Text>
          <TextInput
            value={body}
            onChangeText={next => setBody(next.slice(0, MAX_CHARS))}
            placeholder={
              disputing
                ? 'What was wrong with the work, and what would put it right?'
                : 'Tell us what went wrong, and what you expected'
            }
            placeholderTextColor={colors.inkSubtle}
            multiline
            style={[styles.field, styles.fieldTall]}
            accessibilityLabel="Details"
          />
        </View>

        {/* -------------------------------------------------------- photos */}
        <View style={styles.block}>
          <PhotoStrip
            photos={photos}
            max={MAX_PHOTOS}
            onAdd={() => attach('library')}
            onCapture={() => attach('camera')}
            onRemove={photo =>
              setPhotos(current => current.filter(item => item.id !== photo.id))
            }
          />

          {cameraBlocked ? (
            <Text variant="caption" color={colors.danger}>
              Camera access is off for Homingo. Turn it on in your phone's
              Settings, or upload a photo from your library instead.
            </Text>
          ) : null}
        </View>

        {/* ------------------------------------------------------ evidence */}
        {disputing ? (
          <View style={styles.evidence}>
            <View style={styles.evidenceHead}>
              <Icon name="shieldCheck" size={16} color={colors.primary} />
              <Text variant="labelSm" color={colors.inkBody}>
                What we will look at
              </Text>
            </View>
            {disputeEvidence.map(item => (
              <View key={item} style={styles.evidenceRow}>
                <Icon name="check" size={13} color={colors.primary} strokeWidth={2.4} />
                <Text variant="caption" color={colors.inkBody} style={styles.grow}>
                  {item}
                </Text>
              </View>
            ))}
            <Text variant="caption" color={colors.inkMuted}>
              A completed job is disputed rather than cancelled, so tell us what
              was wrong with the work itself.
            </Text>
          </View>
        ) : null}

        <Button
          label={disputing ? 'Submit dispute' : 'Send request'}
          disabled={!ready}
          onPress={submit}
        />
        <Text variant="caption" align="center" color={colors.inkMuted}>
          {ready
            ? 'We reply within one working day.'
            : 'Add a subject and a few words so we can look into it.'}
        </Text>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.md,
    paddingBottom: 120,
  },
  about: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  block: {
    gap: spacing.sm,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  optionOn: {
    borderColor: colors.primary,
  },
  mark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.borderStrong,
  },
  radioOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  field: {
    ...inputText,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  fieldTall: {
    minHeight: 120,
    textAlignVertical: 'top',
  },
  evidence: {
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surfaceAccent,
  },
  evidenceHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  evidenceRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  grow: {
    flex: 1,
  },
  pressed: {
    opacity: 0.7,
  },
});

export default NewTicketScreen;
